import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import FacebookPixel from './components/FacebookPixel';
import HtmlPixelSnippet from './components/HtmlPixelSnippet';
import TikTokPixel from './components/TikTokPixel';
import { NotificationManager } from './components/Notification';
import ErrorBoundary from './components/ErrorBoundary';
import { ShopProvider, useShop } from './context/ShopContext';
import { getAdminAccessStatus } from './config/admin';
import { auth } from './config/firebase';
import SEO, { HomeSEO, ProductsSEO, ProductDetailSEO, CheckoutSEO } from './components/SEO';
import OfflineNotification from './components/OfflineNotification';
import './App.css';

const Home = lazy(() => import('./pages/Customer/Home'));
const Products = lazy(() => import('./pages/Customer/Products'));
const ProductDetail = lazy(() => import('./pages/Customer/ProductDetail'));
const CategoryPage = lazy(() => import('./pages/Customer/CategoryPage'));
const Checkout = lazy(() => import('./pages/Customer/Checkout'));
const About = lazy(() => import('./pages/Customer/About'));
const AdminLogin = lazy(() => import('./pages/Admin/AdminLogin'));
const Dashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminAccessDenied = lazy(() => import('./pages/Admin/AdminAccessDenied'));
const ProductForm = lazy(() => import('./pages/Admin/ProductForm'));

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold border-t-transparent" />
  </div>
);

const NotFound = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
    <div className="rounded-xl bg-white p-8 text-center shadow-lg">
      <h1 className="mb-2 text-2xl font-bold text-gray-900">الصفحة غير موجودة</h1>
      <p className="text-gray-600">الرابط الذي فتحته غير متاح.</p>
    </div>
  </div>
);

const AdminGuard = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setStatus('unauthenticated');
        return;
      }

      const adminStatus = await getAdminAccessStatus(user.uid, user.email || '');
      setStatus(adminStatus.isAdmin ? 'authorized' : 'forbidden');
    });

    return unsubscribe;
  }, []);

  if (status === 'loading') {
    return <RouteFallback />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/admin" replace state={{ from: location.pathname }} />;
  }

  if (status === 'forbidden') {
    return <Navigate to="/admin/access-denied" replace state={{ reason: 'forbidden' }} />;
  }

  return children;
};

const AppRoutes = () => {
  const { settings } = useShop();

  return (
    <>
      <HomeSEO />
      {settings.facebookPixelSnippet?.trim() ? (
        <HtmlPixelSnippet snippet={settings.facebookPixelSnippet} />
      ) : (
        <FacebookPixel pixelId={settings.facebookPixelId} />
      )}
      {settings.tiktokPixelSnippet?.trim() ? (
        <HtmlPixelSnippet snippet={settings.tiktokPixelSnippet} />
      ) : (
        <TikTokPixel pixelId={settings.tiktokPixelId} />
      )}
      <NotificationManager />
      <OfflineNotification />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<>
            <HomeSEO />
            <Home />
          </>} />
          <Route path="/products" element={<>
            <ProductsSEO />
            <Products />
          </>} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/category/:categoryName" element={<>
            <ProductsSEO />
            <CategoryPage />
          </>} />
          <Route path="/checkout" element={<>
            <CheckoutSEO />
            <Checkout />
          </>} />
          <Route path="/checkout/product/:productId" element={<>
            <CheckoutSEO />
            <Checkout />
          </>} />
          <Route path="/about" element={<>
            <SEO title="من نحن" description="تعرف على متجر Fournitures وخدماتنا" />
            <About />
          </>} />

          <Route path="/admin" element={<>
            <SEO title="تسجيل دخول المسؤول" noindex={true} />
            <AdminLogin />
          </>} />
          <Route
            path="/admin/dashboard"
            element={
              <AdminGuard>
                <SEO title="لوحة التحكم" noindex={true} />
                <Dashboard />
              </AdminGuard>
            }
          />
          <Route path="/admin/access-denied" element={<>
            <SEO title="وصول مرفوض" noindex={true} />
            <AdminAccessDenied />
          </>} />
          <Route
            path="/admin/products/new"
            element={
              <AdminGuard>
                <SEO title="إضافة منتج" noindex={true} />
                <ProductForm />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/products/edit/:id"
            element={
              <AdminGuard>
                <SEO title="تعديل منتج" noindex={true} />
                <ProductForm />
              </AdminGuard>
            }
          />

          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <ShopProvider>
      <AppRoutes />
    </ShopProvider>
  </ErrorBoundary>
);

export default App;
